import {useRecentSyncsQuery} from '@transactions/graphql';
import {AlertCircle, CheckCircle} from 'lucide-react';
import * as React from 'react';
import {SectionCard} from 'src/AccountsView/AccountSection';
import {formatRelativeTime} from 'src/ConnectionsView/formatRelativeTime';
import {cn} from 'src/lib/utils';
import type {RecentSyncsQuery} from '@transactions/graphql';

type SyncRowProps = {
  sync: RecentSyncsQuery['recentSyncs'][number];
};

const SyncRow = ({sync}: SyncRowProps) => {
  const failed = sync.results.filter((result) => result.status !== 'success');
  const hasError = sync.tokensFailed > 0;
  const StatusIcon = hasError ? AlertCircle : CheckCircle;
  // Name the connections that failed when the session recorded them.
  const failedLabel =
    failed.length > 0
      ? `${failed.map((result) => result.institutionName).join(', ')} failed`
      : `${sync.tokensFailed} connection${sync.tokensFailed === 1 ? '' : 's'} failed`;

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 sm:px-4">
      <StatusIcon
        className={cn(
          'size-4 shrink-0',
          hasError ? 'text-amber-500' : 'text-green-600 dark:text-green-500',
        )}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-foreground text-sm">
          {formatRelativeTime(sync.startedAt)}
        </span>
        {hasError && (
          <span className="text-muted-foreground truncate text-xs">
            {failedLabel}
          </span>
        )}
      </div>
      <span className="text-muted-foreground shrink-0 text-sm tabular-nums">
        {sync.transactionsCreated > 0
          ? `+${sync.transactionsCreated} transaction${sync.transactionsCreated === 1 ? '' : 's'}`
          : 'No new transactions'}
      </span>
    </div>
  );
};

/** Sync history, shown at the foot of the Connections page. */
export const RecentSyncs = () => {
  const {data, loading} = useRecentSyncsQuery({
    variables: {limit: 5},
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) return null;
  if (!data?.recentSyncs || data.recentSyncs.length === 0) return null;

  return (
    <section className="flex flex-col gap-1.5">
      <h2 className="text-foreground/80 m-0 flex min-h-8 items-center px-1 text-sm font-semibold">
        Recent syncs
      </h2>
      <SectionCard>
        {data.recentSyncs.map((sync) => (
          <SyncRow key={sync._id} sync={sync} />
        ))}
      </SectionCard>
    </section>
  );
};
